import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Users, Upload, Send, RefreshCw, Trash2, Plus, Mail, MessageCircle, CheckCircle2, X, Megaphone } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, helper, id }) => (
  <div className="glass-card p-5" data-testid={id}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
    {helper && <p className="mt-2 text-xs text-text-muted">{helper}</p>}
  </div>
);

export const CRMPanel = ({ authFetch }) => {
  const [status, setStatus] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newC, setNewC] = useState({ name: '', phone: '', email: '', tags: '', consent: false });
  const [camp, setCamp] = useState({ name: '', channel: 'whatsapp', subject: '', message: '', tags: '', only_consent: true });
  const fileRef = useRef(null);

  const load = useCallback(async (notify = false) => {
    setLoading(true);
    const results = await Promise.allSettled([
      authFetch(`${API}/crm/status`),
      authFetch(`${API}/crm/contacts?search=${encodeURIComponent(search)}`),
      authFetch(`${API}/crm/campaigns`),
    ]);
    try { if (results[0].status === 'fulfilled') setStatus(await results[0].value.json()); } catch (e) { /* noop */ }
    try { if (results[1].status === 'fulfilled') { const cj = await results[1].value.json(); setContacts(cj.contacts || []); setTotal(cj.total || 0); } } catch (e) { /* noop */ }
    try { if (results[2].status === 'fulfilled') setCampaigns((await results[2].value.json()).campaigns || []); } catch (e) { /* noop */ }
    setLoading(false);
    if (notify) toast.success('CRM actualizado');
  }, [authFetch, search]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [load]);

  const importFile = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const r = await authFetch(`${API}/crm/contacts/import`, { method: 'POST', body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Error al importar');
      toast.success(`Importado: ${j.inserted} nuevos, ${j.updated} actualizados, ${j.skipped} omitidos`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const addContact = async () => {
    if (!newC.phone && !newC.email) { toast.error('Teléfono o correo requerido'); return; }
    try {
      setBusy('new');
      const r = await authFetch(`${API}/crm/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newC, tags: newC.tags.split(',').map((t) => t.trim()).filter(Boolean) }) });
      if (!r.ok) throw new Error('No se pudo crear');
      toast.success('Contacto agregado');
      setShowNew(false); setNewC({ name: '', phone: '', email: '', tags: '', consent: false }); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const delContact = async (id) => {
    try { setBusy(id); await authFetch(`${API}/crm/contacts/${id}`, { method: 'DELETE' }); await load(); }
    catch (e) { toast.error('Error'); } finally { setBusy(null); }
  };

  const createCampaign = async () => {
    if (!camp.name.trim() || !camp.message.trim()) { toast.error('Nombre y mensaje requeridos'); return; }
    try {
      setBusy('camp');
      const r = await authFetch(`${API}/crm/campaigns`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...camp, tags: camp.tags.split(',').map((t) => t.trim()).filter(Boolean) }) });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Error');
      toast.success(`Campaña creada · audiencia ${j.audience_count}`);
      setCamp({ name: '', channel: 'whatsapp', subject: '', message: '', tags: '', only_consent: true }); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const sendCampaign = async (id) => {
    try {
      setBusy(id);
      const r = await authFetch(`${API}/crm/campaigns/${id}/send`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Error al enviar');
      toast.success(`Enviados ${j.sent} · fallidos ${j.failed}`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const st = status?.stats || {};

  return (
    <section className="space-y-6" data-testid="crm-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-text-muted">CRM Hiperconectado</p>
          <h3 className="mt-1 text-lg font-bold text-text-heading">Contactos y campañas masivas</h3>
          <p className="mt-1 text-xs text-text-muted">Sube tu Excel/CSV, gestiona contactos y lanza campañas por WhatsApp y correo. WhatsApp solo a quien dio consentimiento.</p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading} data-testid="crm-refresh"
          className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-4 py-2 text-xs font-semibold text-text-muted transition hover:text-text-heading">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Stat label="Contactos" value={st.contacts ?? 0} helper="en tu base" id="crm-stat-contacts" />
        <Stat label="Con consentimiento" value={st.with_consent ?? 0} helper="aptos para WhatsApp" id="crm-stat-consent" />
        <Stat label="Con correo" value={st.with_email ?? 0} helper="aptos para email" id="crm-stat-email" />
        <Stat label="Campañas" value={st.campaigns ?? 0} helper="creadas" id="crm-stat-campaigns" />
      </div>

      {/* Importar */}
      <div className="glass-card p-5" data-testid="crm-import">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-heading"><Upload className="h-4 w-4 text-primary" /> Importar contactos (Excel / CSV)</h4>
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" data-testid="crm-import-input"
            onChange={(e) => importFile(e.target.files?.[0])} disabled={uploading}
            className="text-xs text-text-muted file:mr-3 file:rounded-full file:border-0 file:bg-primary file:px-4 file:py-2 file:text-xs file:font-semibold file:text-background" />
          {uploading && <span className="flex items-center gap-1 text-xs text-text-muted"><RefreshCw className="h-3 w-3 animate-spin" /> Importando…</span>}
        </div>
        <p className="mt-2 text-[11px] text-text-muted">Columnas reconocidas: nombre, teléfono/whatsapp, correo, etiquetas, consentimiento.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Contactos */}
        <div className="glass-card p-5" data-testid="crm-contacts-card">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-text-heading"><Users className="h-4 w-4 text-primary" /> Contactos <span className="text-text-muted">({total})</span></h4>
            <button type="button" onClick={() => setShowNew(!showNew)} data-testid="crm-new-toggle" className="inline-flex items-center gap-1 rounded-full border border-border-subtle px-3 py-1.5 text-[11px] font-semibold text-text-muted hover:text-text-heading"><Plus className="h-3.5 w-3.5" /> Nuevo</button>
          </div>
          {showNew && (
            <div className="mb-3 space-y-2 rounded-xl border border-border-subtle bg-background/50 p-3" data-testid="crm-new-form">
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Nombre" value={newC.name} onChange={(e) => setNewC({ ...newC, name: e.target.value })} data-testid="crm-new-name" className="rounded-lg border border-border-subtle bg-background/70 p-2 text-xs text-text-heading" />
                <input placeholder="+51..." value={newC.phone} onChange={(e) => setNewC({ ...newC, phone: e.target.value })} data-testid="crm-new-phone" className="rounded-lg border border-border-subtle bg-background/70 p-2 text-xs text-text-heading" />
                <input placeholder="correo" value={newC.email} onChange={(e) => setNewC({ ...newC, email: e.target.value })} data-testid="crm-new-email" className="rounded-lg border border-border-subtle bg-background/70 p-2 text-xs text-text-heading" />
                <input placeholder="etiquetas, separadas" value={newC.tags} onChange={(e) => setNewC({ ...newC, tags: e.target.value })} data-testid="crm-new-tags" className="rounded-lg border border-border-subtle bg-background/70 p-2 text-xs text-text-heading" />
              </div>
              <label className="flex items-center gap-2 text-xs text-text-muted"><input type="checkbox" checked={newC.consent} onChange={(e) => setNewC({ ...newC, consent: e.target.checked })} data-testid="crm-new-consent" /> Dio consentimiento para WhatsApp</label>
              <button type="button" onClick={addContact} disabled={busy === 'new'} data-testid="crm-new-save" className="w-full rounded-full bg-primary px-4 py-2 text-xs font-semibold text-background">Guardar</button>
            </div>
          )}
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar nombre, teléfono o correo" data-testid="crm-search"
            className="mb-3 w-full rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading focus:border-primary focus:outline-none" />
          <div className="max-h-80 space-y-2 overflow-auto" data-testid="crm-contacts-list">
            {contacts.map((c) => (
              <div key={c.contact_id} className="flex items-center justify-between gap-2 rounded-xl border border-border-subtle bg-background/50 p-2.5" data-testid={`crm-contact-${c.contact_id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text-heading">{c.name || '(sin nombre)'}</p>
                  <p className="truncate text-[11px] text-text-muted">{c.phone} {c.email ? `· ${c.email}` : ''} {c.tags?.length ? `· ${c.tags.join(', ')}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  {c.consent ? <CheckCircle2 className="h-4 w-4 text-emerald-400" title="Consentimiento" /> : <X className="h-4 w-4 text-text-muted" title="Sin consentimiento" />}
                  <button type="button" onClick={() => delContact(c.contact_id)} disabled={busy === c.contact_id} data-testid={`crm-contact-del-${c.contact_id}`} className="text-rose-300 hover:text-rose-200"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            {!contacts.length && <p className="text-sm text-text-muted" data-testid="crm-contacts-empty">Sin contactos. Importa un Excel o agrega uno.</p>}
          </div>
        </div>

        {/* Campañas */}
        <div className="glass-card p-5" data-testid="crm-campaigns-card">
          <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-heading"><Megaphone className="h-4 w-4 text-primary" /> Nueva campaña masiva</h4>
          <div className="space-y-2">
            <input placeholder="Nombre de la campaña" value={camp.name} onChange={(e) => setCamp({ ...camp, name: e.target.value })} data-testid="crm-camp-name" className="w-full rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading" />
            <div className="flex gap-2">
              <button type="button" onClick={() => setCamp({ ...camp, channel: 'whatsapp' })} data-testid="crm-camp-wa" className={`flex-1 inline-flex items-center justify-center gap-1 rounded-full px-3 py-2 text-xs font-semibold transition ${camp.channel === 'whatsapp' ? 'bg-emerald-500 text-background' : 'border border-border-subtle text-text-muted'}`}><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</button>
              <button type="button" onClick={() => setCamp({ ...camp, channel: 'email' })} data-testid="crm-camp-email" className={`flex-1 inline-flex items-center justify-center gap-1 rounded-full px-3 py-2 text-xs font-semibold transition ${camp.channel === 'email' ? 'bg-sky-500 text-background' : 'border border-border-subtle text-text-muted'}`}><Mail className="h-3.5 w-3.5" /> Correo</button>
            </div>
            {camp.channel === 'email' && (
              <input placeholder="Asunto del correo" value={camp.subject} onChange={(e) => setCamp({ ...camp, subject: e.target.value })} data-testid="crm-camp-subject" className="w-full rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading" />
            )}
            <textarea rows={3} placeholder="Mensaje. Usa {nombre} para personalizar." value={camp.message} onChange={(e) => setCamp({ ...camp, message: e.target.value })} data-testid="crm-camp-message" className="w-full rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading" />
            <input placeholder="Segmento por etiquetas (vacío = todos)" value={camp.tags} onChange={(e) => setCamp({ ...camp, tags: e.target.value })} data-testid="crm-camp-tags" className="w-full rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading" />
            <button type="button" onClick={createCampaign} disabled={busy === 'camp'} data-testid="crm-camp-create" className="w-full rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-background">Crear campaña</button>
          </div>

          <div className="mt-4 space-y-2" data-testid="crm-campaigns-list">
            {campaigns.map((cp) => (
              <div key={cp.campaign_id} className="flex items-center justify-between gap-2 rounded-xl border border-border-subtle bg-background/50 p-3" data-testid={`crm-campaign-${cp.campaign_id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text-heading">{cp.name} <span className="text-[10px] text-text-muted">· {cp.channel}</span></p>
                  <p className="truncate text-[11px] text-text-muted">Audiencia {cp.audience_count} · enviados {cp.sent} · fallidos {cp.failed}</p>
                </div>
                {cp.status === 'sent'
                  ? <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-200">Enviada</span>
                  : <button type="button" onClick={() => sendCampaign(cp.campaign_id)} disabled={busy === cp.campaign_id} data-testid={`crm-campaign-send-${cp.campaign_id}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-background disabled:opacity-50"><Send className="h-3.5 w-3.5" /> Enviar</button>}
              </div>
            ))}
            {!campaigns.length && <p className="text-sm text-text-muted" data-testid="crm-campaigns-empty">Sin campañas todavía.</p>}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CRMPanel;
